import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/JWTAuthContext';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(3, 0),
}));

const MeetingListings = () => {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: '',
    searchTerm: '',
    type: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Mock data for demonstration
  const mockMeetings = [
    {
      id: 1,
      title: 'Property Viewing - Downtown Apartment',
      clientName: 'Sarah Wilson',
      clientEmail: 'sarah.wilson@email.com',
      clientPhone: '+1234567890',
      salesPerson: 'John Smith',
      status: 'Scheduled',
      date: '2024-01-20',
      time: '14:00',
      duration: '60',
      location: '123 Main St, Downtown',
      type: 'Property Viewing',
      notes: 'Client interested in 2-bedroom apartment',
      priority: 'High',
    },
    {
      id: 2,
      title: 'Contract Discussion - Villa Sale',
      clientName: 'Michael Brown',
      clientEmail: 'michael.brown@email.com',
      clientPhone: '+1234567891',
      salesPerson: 'Jane Doe',
      status: 'Completed',
      date: '2024-01-18',
      time: '10:30',
      duration: '90',
      location: 'Office - Conference Room A',
      type: 'Contract Discussion',
      notes: 'Contract signed successfully',
      priority: 'Medium',
    },
    {
      id: 3,
      title: 'Initial Consultation - New Client',
      clientName: 'Emily Davis',
      clientEmail: 'emily.davis@email.com',
      clientPhone: '+1234567892',
      salesPerson: 'Mike Johnson',
      status: 'Pending',
      date: '2024-01-25',
      time: '16:00',
      duration: '45',
      location: 'Coffee Shop - Downtown',
      type: 'Initial Consultation',
      notes: 'New client looking for investment property',
      priority: 'Low',
    },
    {
      id: 4,
      title: 'Follow-up Meeting - Penthouse',
      clientName: 'David Miller',
      clientEmail: 'david.miller@email.com',
      clientPhone: '+1234567893',
      salesPerson: 'Lisa Chen',
      status: 'Cancelled',
      date: '2024-01-22',
      time: '11:00',
      duration: '60',
      location: 'Property Site - Penthouse Unit',
      type: 'Follow-up',
      notes: 'Client cancelled due to schedule conflict',
      priority: 'Medium',
    },
  ];

  useEffect(() => {
    fetchMeetings();
  }, [filters]);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call
      // const response = await getMeetingsList(filters);
      // setMeetings(response.data);
      
      // Using mock data for now
      setTimeout(() => {
        setMeetings(mockMeetings);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load meetings',
        severity: 'error',
      });
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleViewMeeting = (meeting) => {
    setSelectedMeeting(meeting);
    setDialogOpen(true);
  };

  const handleEditMeeting = (meeting) => {
    // TODO: Implement edit functionality
    console.log('Edit meeting:', meeting);
  };

  const handleDeleteMeeting = (meeting) => {
    // TODO: Implement delete functionality
    console.log('Delete meeting:', meeting);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedMeeting(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusChip = (status) => {
    const statusColors = {
      'Scheduled': 'info',
      'Completed': 'success',
      'Pending': 'warning',
      'Cancelled': 'error',
      'Rescheduled': 'secondary',
    };
    return <Chip label={status} color={statusColors[status] || 'default'} size="small" />;
  };

  const getPriorityChip = (priority) => {
    const priorityColors = {
      'High': 'error',
      'Medium': 'warning',
      'Low': 'success',
    };
    return <Chip label={priority} color={priorityColors[priority] || 'default'} size="small" />;
  };

  const getTypeChip = (type) => {
    const typeColors = {
      'Property Viewing': 'primary',
      'Contract Discussion': 'secondary',
      'Initial Consultation': 'info',
      'Follow-up': 'success',
    };
    return <Chip label={type} color={typeColors[type] || 'default'} size="small" />;
  };

  return (
    <Container maxWidth="xl">
      <StyledPaper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Meeting Listings
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => console.log('Add new meeting')}
          >
            New Meeting
          </Button>
        </Box>

        {/* Filters Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Filters</Typography>
              <Button
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                sx={{ ml: 'auto' }}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>
            </Box>
            
            {showFilters && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Search"
                    name="searchTerm"
                    value={filters.searchTerm}
                    onChange={handleFilterChange}
                    placeholder="Search by title, client name, or location"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={filters.status}
                      onChange={handleFilterChange}
                      label="Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="Scheduled">Scheduled</MenuItem>
                      <MenuItem value="Completed">Completed</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Cancelled">Cancelled</MenuItem>
                      <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      name="type"
                      value={filters.type}
                      onChange={handleFilterChange}
                      label="Type"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="Property Viewing">Property Viewing</MenuItem>
                      <MenuItem value="Contract Discussion">Contract Discussion</MenuItem>
                      <MenuItem value="Initial Consultation">Initial Consultation</MenuItem>
                      <MenuItem value="Follow-up">Follow-up</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Date From"
                    name="dateFrom"
                    type="date"
                    value={filters.dateFrom}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Date To"
                    name="dateTo"
                    type="date"
                    value={filters.dateTo}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Meetings Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Sales Person</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {meetings.map((meeting) => (
                <TableRow key={meeting.id}>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {meeting.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                        <PersonIcon />
                      </Avatar>
                      <div>
                        <div>{meeting.clientName}</div>
                        <div style={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                          {meeting.clientEmail}
                        </div>
                      </div>
                    </Box>
                  </TableCell>
                  <TableCell>{meeting.salesPerson}</TableCell>
                  <TableCell>
                    <div>{new Date(meeting.date).toLocaleDateString()}</div>
                    <div style={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {meeting.time} ({meeting.duration} min)
                    </div>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <span style={{ fontSize: '0.875rem' }}>{meeting.location}</span>
                    </Box>
                  </TableCell>
                  <TableCell>{getTypeChip(meeting.type)}</TableCell>
                  <TableCell>{getStatusChip(meeting.status)}</TableCell>
                  <TableCell>{getPriorityChip(meeting.priority)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewMeeting(meeting)}
                      title="View Details"
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditMeeting(meeting)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMeeting(meeting)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {meetings.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No meetings found
            </Typography>
          </Box>
        )}
      </StyledPaper>

      {/* Meeting Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Meeting Details</DialogTitle>
        <DialogContent>
          {selectedMeeting && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>{selectedMeeting.title}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Client</Typography>
                <Typography variant="body1">{selectedMeeting.clientName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedMeeting.clientEmail}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedMeeting.clientPhone}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Sales Person</Typography>
                <Typography variant="body1">{selectedMeeting.salesPerson}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Date & Time</Typography>
                <Typography variant="body1">
                  {new Date(selectedMeeting.date).toLocaleDateString()} at {selectedMeeting.time}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Duration: {selectedMeeting.duration} minutes
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                <Typography variant="body1">{selectedMeeting.location}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                {getTypeChip(selectedMeeting.type)}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                {getStatusChip(selectedMeeting.status)}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Priority</Typography>
                {getPriorityChip(selectedMeeting.priority)}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                <Typography variant="body1">
                  {selectedMeeting.notes || 'No notes provided'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              handleEditMeeting(selectedMeeting);
              handleCloseDialog();
            }}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MeetingListings; 